import { resolveId } from '@medplum/core'
import type { CarePlan } from '@medplum/fhirtypes'
import type { ResourceChangeSet } from '@seen/core/queued-edits/model'
import type { JSX } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { useCarePlanDeepLink } from '#src/components/queued-edits/useCarePlanDeepLink.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'
import { resourceDeepLink } from '#src/utils/resourceDeepLink.ts'

// The resource title. Deep-links into the resource's detail page when a route
// is known (so reviewers can open it in context, cmd-click for a new tab);
// otherwise renders as plain text. Titles derived from HTML-authored fields
// (care plan goals, order descriptions) are stripped to plain text.
export function ResourceLabel({
  change,
}: {
  change: ResourceChangeSet
}): JSX.Element {
  // CarePlans (goals/interventions/initial) need a parent-aware link that a
  // synchronous resourceDeepLink can't build — resolve it in a dedicated child.
  if (change.resource?.resourceType === 'CarePlan') {
    return <CarePlanResourceLabel change={change} carePlan={change.resource} />
  }
  const href = resourceDeepLink({
    resourceType: change.resourceType,
    resourceId: change.resourceId,
    patientReference: change.patientReference,
  })
  return <LabelText label={change.label} href={href} />
}

function CarePlanResourceLabel({
  change,
  carePlan,
}: {
  change: ResourceChangeSet
  carePlan: CarePlan
}): JSX.Element {
  const { href, isResolving } = useCarePlanDeepLink(
    carePlan,
    change.resourceId,
    resolveId({ reference: change.patientReference })
  )
  // Render as plain text while the parent care plan(s) resolve, then upgrade to
  // the link — avoids briefly linking to the wrong (own-id) page.
  return <LabelText label={change.label} href={isResolving ? null : href} />
}

function LabelText({
  label,
  href,
}: {
  label: string
  href: string | null
}): JSX.Element {
  const text = extractTextFromHTML(label)
  if (!href) {
    return (
      <span className="min-w-0 truncate text-sm font-semibold">{text}</span>
    )
  }
  return (
    <Link
      to={href}
      target="_blank"
      className="min-w-0 truncate text-sm font-semibold hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {text}
    </Link>
  )
}
